import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, ChevronDown, Award, FileText, Building2, Search, ShoppingBag } from 'lucide-react';
import { companyInfo, services } from '../data/mock';
import ContactModal from './ContactModal';

// Services Dropdown Component
const ServicesDropdown = ({ services, serviceIcons }) => {
  const [activeService, setActiveService] = useState(services[0]);

  return (
    <div className="fixed left-0 right-0 top-[72px] bg-white shadow-2xl border-t border-gray-100 animate-scale-in z-50" style={{ height: '35vh', minHeight: '400px' }}>
      <div className="h-full flex">
        {/* Left Side - Services List */}
        <div className="w-1/3 bg-gradient-to-br from-[#4B0082]/5 to-[#FF7F7F]/5 p-6 pb-8 overflow-hidden">
          <h3 className="text-lg font-bold text-[#4B0082] mb-4">Our Services</h3>
          <div className="space-y-2">
            {services.map((service) => {
              const Icon = serviceIcons[service.slug];
              return (
                <Link
                  key={service.id}
                  to={`/services/${service.slug}`}
                  onMouseEnter={() => setActiveService(service)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                    activeService.id === service.id
                      ? 'bg-[#FF7F7F] text-white shadow-lg'
                      : 'hover:bg-white/50 text-gray-700'
                  }`}
                >
                  <Icon className={`h-5 w-5 flex-shrink-0 ${
                    activeService.id === service.id ? 'text-white' : 'text-[#FF7F7F]'
                  }`} />
                  <span className="font-medium text-sm">{service.title}</span>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Right Side - Service Details */}
        <div className="flex-1 p-8 flex gap-6">
          {/* Description */}
          <div className="flex-1 flex flex-col justify-center">
            <h2 className="text-3xl font-bold text-[#4B0082] mb-4">
              {activeService.title}
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              {activeService.fullDescription || activeService.description}
            </p>
            <div>
              <Link
                to={`/services/${activeService.slug}`}
                className="inline-flex items-center px-6 py-3 bg-[#FF7F7F] hover:bg-[#FF6B6B] text-white font-semibold rounded-lg transition-all hover:shadow-lg"
              >
                Learn More
                <ChevronDown className="ml-2 h-4 w-4 transform -rotate-90" />
              </Link>
            </div>
          </div>

          {/* Image */}
          <div className="w-1/3 flex items-center">
            <div className="relative w-full h-full rounded-xl overflow-hidden shadow-xl">
              <img
                src={activeService.image}
                alt={activeService.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#4B0082]/40 to-transparent"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const location = useLocation();

  const serviceIcons = {
    'trademark-registration': Award,
    'copyright-registration': FileText,
    'company-formation': Building2,
    'trademark-search': Search,
    'amazon-brand-registry': ShoppingBag
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="/assets/img/brand/logo-dark.png" 
              alt={companyInfo.name}
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors hover:text-[#FF7F7F] ${
                isActive('/') ? 'text-[#FF7F7F]' : 'text-gray-700'
              }`}
            >
              Home
            </Link>
            <Link
              to="/about"
              className={`text-sm font-medium transition-colors hover:text-[#FF7F7F] ${
                isActive('/about') ? 'text-[#FF7F7F]' : 'text-gray-700'
              }`}
            >
              About Us
            </Link>
            
            {/* Services Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={(e) => {
                // Only close if not moving to the dropdown
                const rect = e.currentTarget.getBoundingClientRect();
                if (e.clientY > rect.bottom + 10) {
                  setIsServicesOpen(false);
                }
              }}
            >
              <button
                className="flex items-center text-sm font-medium text-gray-700 hover:text-[#FF7F7F] transition-colors"
              >
                Services
                <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isServicesOpen && (
                <div
                  onMouseEnter={() => setIsServicesOpen(true)}
                  onMouseLeave={() => setIsServicesOpen(false)}
                >
                  <ServicesDropdown 
                    services={services} 
                    serviceIcons={serviceIcons}
                  />
                </div>
              )}
            </div>

            <Link
              to="/contact"
              className={`text-sm font-medium transition-colors hover:text-[#FF7F7F] ${
                isActive('/contact') ? 'text-[#FF7F7F]' : 'text-gray-700'
              }`}
            >
              Contact
            </Link>

            <Button
              style={{ backgroundColor: '#FF7F7F' }}
              className="hover:opacity-90 transition-opacity"
              onClick={() => setIsModalOpen(true)}
            >
              Get Started - $45
            </Button>
          </nav>

          {/* Contact Modal */}
          <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? (
              <X className="h-6 w-6 text-[#3A2E1F]" />
            ) : (
              <Menu className="h-6 w-6 text-[#3A2E1F]" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t">
            <div className="flex flex-col space-y-4">
              <Link
                to="/"
                className="text-sm font-medium text-gray-700 hover:text-[#FF7F7F]"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/about"
                className="text-sm font-medium text-gray-700 hover:text-[#FF7F7F]"
                onClick={() => setIsMenuOpen(false)}
              >
                About Us
              </Link>
              
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700">Services</div>
                <div className="pl-4 space-y-2">
                  {services.map((service) => (
                    <Link
                      key={service.id}
                      to={`/services/${service.slug}`}
                      className="block text-sm text-gray-600 hover:text-[#FF7F7F]"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {service.title}
                    </Link>
                  ))}
                </div>
              </div>

              <Link
                to="/contact"
                className="text-sm font-medium text-gray-700 hover:text-[#FF7F7F]"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </Link>

              <Button
                style={{ backgroundColor: '#FF7F7F' }}
                className="w-full hover:opacity-90 transition-opacity"
                onClick={() => {
                  setIsMenuOpen(false);
                  setIsModalOpen(true);
                }}
              >
                Get Started - $45
              </Button>
            </div>
          </div>
        )}
        
        {/* Contact Modal */}
        <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      </div>
    </header>
  );
};

export default Header;